'use client';

import React, { useEffect } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { text4 } from '@osmosis/tailwind-ui/styles/text';
import {
  LoggedOutFullLibraryData,
  LoggedOutLibraryData,
  LoggedOutProductLibraryContainsItem,
  SelectedLibraryItem,
} from '../../types/library';
import LibrarySidebarLoggedOut from './LibrarySidebarLoggedOut';
import LibraryTopicBoxLoggedOut from './LibraryTopicBoxLoggedOut';
import LibraryHeaderLoggedOut from './LibraryHeaderLoggedOut';
import { useIntl } from 'react-intl';
import { track } from '../../lib/util/tracking';
import { PrimeBanner } from '../PrimeBanner';
import { getStorePages } from '@osmosis/shared/products';
import { LanguageCode } from '@osmosis/shared/languages';
import {
  LanguageProvider,
  useLanguageContext,
} from '../../context/LanguageProvider';
import { usePagesContext } from '../../context/PagesProvider';
import { useContentSize } from '@osmosis/nextjs-components/context';
import ProductDropdown from './logged-out/ProductDropdown';
import { SCROLL_TOP_CLASSNAME } from '../../constants/library';
import PublicHeroSearch from '../search/PublicHeroSearch';

type Props = {
  libraryData: LoggedOutLibraryData;
  library: LoggedOutFullLibraryData;
  onSelect: (
    l1: LoggedOutProductLibraryContainsItem,
    l2: LoggedOutProductLibraryContainsItem,
  ) => void;
  selectedItem?: SelectedLibraryItem;
  openIndex?: number;
  targetId?: string;
};

/**
 * LibraryWrapper.
 */
const LibraryWrapper = (props: Props) => {
  const { libraryData, library, onSelect, selectedItem, openIndex, targetId } =
    props;

  const navItems = libraryData?.fullLibrary[0]?.productLibrary || [];

  const headerData = libraryData?.metadata;

  const { messages } = useIntl();
  const {
    product: { key: productKey },
  } = library;

  const { activeLanguage, storeType } = useLanguageContext();

  const { query, push } = usePagesContext();

  useEffect(() => {
    if (library) {
      const topic = library.productPath?.split('/')?.[1] || false;
      track(
        'library_interaction',
        {
          action: 'visit',
          topic,
        },
        ['keen'],
      );
    } else {
      const productKey =
        libraryData?.productsList?.[0]?.key?.toLowerCase() || '';
      push(`/library/${productKey}`);
    }
  }, [library]);

  const products = getStorePages(activeLanguage, storeType);
  const currentProduct =
    // Try to get the product key from query params first
    products.find((i) => i.key === query.key) ||
    products.find((i) => i.key === library?.product.key);

  // Only show the library in spanish if the library has been translated
  const storePage = products.find((i) => i.key === productKey);
  let libraryMessages = {};
  let libraryActiveLanguage = LanguageCode.EN;

  if (storePage?.translatedLibrary) {
    libraryMessages = messages;
    libraryActiveLanguage = activeLanguage;
  }

  // Responsive layout keyed off the *content* width (not the viewport), so the
  // library collapses to a single column when the Osmosis AI panel narrows the
  // content area. Falls back to the original viewport classes during SSR.
  const { getClassValue } = useContentSize();

  const navContainerResponsive = getClassValue({
    defaultClasses:
      'block lg:grid px-0 lg:px-2 grid-cols-[95%] lg:grid-cols-[360px_auto]',
    breakpoints: {
      lg: 'grid px-2 grid-cols-[360px_auto]',
      base: 'block px-0 grid-cols-[95%]',
    },
  });

  const sidebarContainerResponsive = getClassValue({
    defaultClasses: 'lg:h-[calc(100vh-0px)] h-auto',
    breakpoints: {
      lg: 'h-[calc(100vh-0px)]',
      base: 'h-auto',
    },
  });

  const topicBoxResponsive = getClassValue({
    defaultClasses: 'px-0 lg:px-1 min-w-[100px] lg:min-w-[418px]',
    breakpoints: {
      lg: 'px-1 min-w-[418px]',
      base: 'px-0 min-w-[100px]',
    },
  });

  return library ? (
    <LanguageProvider
      serverIntlMessages={libraryMessages}
      bestLanguage={libraryActiveLanguage}
    >
      <Box cn={`${bg0} ${text4}`} data-testid="product-container">
        <Box data-testid="product-header-container">
          {headerData && <LibraryHeaderLoggedOut headerData={headerData} />}
        </Box>

        {headerData && (
          <PublicHeroSearch
            dataTestId="library-search-section"
            source="Library"
          />
        )}

        <Box
          data-testid="product-nav-container"
          cn={`${bg0} relative grid-rows-[auto] z-0 max-w-[1200px] m-auto ${navContainerResponsive}`}
          style={{
            gridTemplateAreas: `
              "product placesaver"
              "nav content"
            `,
          }}
        >
          <ProductDropdown
            currentProduct={currentProduct}
            products={products}
          />

          <Box
            data-testid="placesaver"
            style={{
              gridArea: 'placesaver',
            }}
          />

          <Box
            data-testid="sidebar-container"
            cn={`sticky top-0 z-3 ${sidebarContainerResponsive}`}
          >
            <LibrarySidebarLoggedOut
              navItems={navItems}
              onSelect={onSelect}
              selectedItem={selectedItem}
              openIndex={openIndex}
            />
          </Box>

          <Box
            data-testid="topic-box-container"
            cn={`m-0 flex-1 ${SCROLL_TOP_CLASSNAME} ${topicBoxResponsive}`}
          >
            <LibraryTopicBoxLoggedOut
              library={library}
              {...(targetId && { targetId })}
            />
          </Box>
        </Box>
        <PrimeBanner />
      </Box>
    </LanguageProvider>
  ) : null;
};

export default LibraryWrapper;
